import { Tabs, Box } from "@radix-ui/themes";
import EntityTab from "@/components/organisms/Account/Tabs/EntityTab";
import Container from "@/components/atoms/Container";
import { Account } from "@/types/Account";
import DepartmentsTab from "./DepartmentsTab";
import CountriesTab from "@/components/organisms/Account/Tabs/CountriesTab";
import FunctionTab from "./FunctionTab";
import TicketTypesTab from "./TicketTypesTab";
import AccountForm from "@/components/organisms/Account/Form";
import { router } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";
import { Threshold } from "@/components/molecules/ThresholdList";
import TransferTab from "./TransferTab";
import { TransferDataType } from "@/components/atoms/TransferList";
import useAccess from "@/hooks/useAccess";

type Props = {
  edit: boolean;
  account: Account;
  props: {
    [key: string]: any | TransferDataType[];
  };
  setEdit: (edit: boolean) => void;
  onTabChange?: (tab: string) => void;
};

const AccountTabs = ({
  edit,
  account: { id, ...account },
  props: {
    all_priorities: priorities,
    all_ticket_statuses: ticketStatuses,
    all_internal_ticket_types: internalTicketTypes,
    all_service_types: serviceTypes,
    all_nmq_entities: nmqEntities,
    redirect_props
  },
  setEdit,
  onTabChange = () => {},
}: Props) => {
  const { getAccess } = useAccess();
  const tabRef = useRef(null);
  const [active, setActive] = useState("account");

  const onSubmit = (data: any) => {
    if (!edit) return;
    
    const formData = new FormData();
  
    Object.entries(data).forEach(([key, value]) => {
      if (key === "avatar" && value instanceof File) {
        formData.append(`account[${key}]`, value);
      } else if (key === "status_inactivity_threshold") {
        (value as Threshold[]).forEach((threshold, index) => {
          formData.append(`account[status_inactivity_threshold][${index}][status]`, threshold.status || "");
          formData.append(`account[status_inactivity_threshold][${index}][threshold]`, threshold.threshold.toString());
        });
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          formData.append(`account[${key}][${index}]`, item);
        });
      } else {
        formData.append(`account[${key}]`, value as string);
      }
    });

    router.put(`/accounts/${id}`, formData, {
      onFinish: () => setEdit(false),
      preserveScroll: true,
      headers: {
        "X-Inertia": "true",
      },
    });
  };

  useEffect(() => {
    if (tabRef?.current)
      [...(tabRef?.current as any).children]
        .find((el: any) => el.dataset.state === "active")
        .scrollIntoView({
          inline: "center",
        });
  }, [active]);

  const handleTabChange = (e: string) => {
    setActive(e);
    onTabChange(e);
  };

  return (
    <Tabs.Root
      value={active}
      onValueChange={handleTabChange}
      className="account-tabs"
    >
      <Tabs.List className={"px-2"} ref={tabRef}>
        <Tabs.Trigger value="account">Account</Tabs.Trigger>
        <Tabs.Trigger value="entities">Entities</Tabs.Trigger>
        <Tabs.Trigger value="countries">Countries</Tabs.Trigger>
        <Tabs.Trigger value="departments">Departments</Tabs.Trigger>
        <Tabs.Trigger value="functions">Functions</Tabs.Trigger>
        <Tabs.Trigger value="ticket_types">Ticket Types</Tabs.Trigger>
        <Tabs.Trigger value="priorities">Priorities</Tabs.Trigger>
        <Tabs.Trigger value="ticket_statuses">Ticket Statuses</Tabs.Trigger>
        <Tabs.Trigger value="service_types">Service Types</Tabs.Trigger>
        <Tabs.Trigger value="internal_ticket_types">
          Internal Ticket Types
        </Tabs.Trigger>
      </Tabs.List>

      <Box>
        <Tabs.Content value="account">
          <Container className="py-4">
            <AccountForm
              edit={edit}
              account={{ id, ...account }}
              onSubmit={onSubmit}
              errors={redirect_props?.errors}
            />
          </Container>
        </Tabs.Content>

        <Tabs.Content value="entities">
          {getAccess(["listing_entities_of_specific_account"]) ? (
            <EntityTab
              accountId={id}
              data={account.entities}
              nmqEntities={nmqEntities}
              redirect_props={redirect_props}
            />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>

        <Tabs.Content value="countries">
          {getAccess(["listing_countries_of_specific_account"]) ? (
            <CountriesTab accountId={id} data={account.countries} redirect_props={redirect_props} />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
          
        </Tabs.Content>

        <Tabs.Content value="departments">
          {getAccess(["listing_departments_of_specific_account"]) ? (
            <DepartmentsTab accountId={id} data={account.departments} redirect_props={redirect_props} />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>

        <Tabs.Content value="functions">
          {getAccess(["listing_functions_of_specific_account"]) ? (
            <FunctionTab accountId={id} data={account.functions} redirect_props={redirect_props} />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>

        <Tabs.Content value="priorities">
          {getAccess(["listing_priorities_of_specific_account"]) ? (
            <TransferTab
              apiURL="set_account_priorities"
              idKey="priority_id"
              accountId={id}
              data={account.priorities}
              sourceData={priorities}
              accessKeys={["update_account_priorities"]}
            />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>

        <Tabs.Content value="ticket_statuses">
          {getAccess(["listing_ticket_statuses_of_specific_account"]) ? (
            <TransferTab
              apiURL="set_account_statuses"
              idKey="ticket_status_id"
              accountId={id}
              data={account.ticket_statuses}
              sourceData={ticketStatuses}
              accessKeys={["update_account_statuses"]}
            />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>

        <Tabs.Content value="ticket_types">
          {getAccess(["listing_ticket_types_of_specific_account"]) ? (
            <TicketTypesTab accountId={id} data={account.ticket_types} redirect_props={redirect_props} />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>

        <Tabs.Content value="service_types">
          {getAccess(["listing_service_types_of_specific_account"]) ? (
            <TransferTab
              apiURL="set_account_service_types"
              idKey="service_type_id"
              accountId={id}
              data={account.service_types}
              sourceData={serviceTypes}
              accessKeys={["update_account_service_types"]}
            />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>

        <Tabs.Content value="internal_ticket_types">
          {getAccess(["listing_internal_ticket_types_of_specific_account"]) ? (
            <TransferTab
              apiURL="set_account_internal_ticket_types"
              idKey="internal_ticket_type_id"
              accountId={id}
              data={account.internal_ticket_types}
              sourceData={internalTicketTypes}
              accessKeys={["update_account_internal_ticket_types"]}
            />
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
              You are not authorized to view this content.
            </span>
          )}
        </Tabs.Content>
      </Box>
    </Tabs.Root>
  );
};

export default AccountTabs;
